import { useCallback, useMemo } from 'react';
import qs from 'qs';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { safeCheckPartnerNameFromCookie } from '$util/cookies';
import getCampaignId from '$util/getCampaignId';
import { isBrowser, storeStartSessionUrl } from '$util/index';
import type { AnyObject } from '$util/types';
import { trimSpacesFromString } from '$util/stringOperations/trimSpacesFromString';
import { getSegmentFromLocale } from '$util/getSegment';
import { getActiveExperiments } from '$util/abTest';

export interface PageTypeProps {
  readonly slug: string;
  readonly sku?: string;
  readonly type: string;
}

export interface UseVrmLookupProps {
  readonly pageType?: PageTypeProps[];
  readonly sku?: string;
  readonly extraParams?: AnyObject;
  readonly targetBlank?: boolean;
  readonly formAction?: string;
  readonly activeTab?: string;
}

interface SessionDefaultParams {
  readonly vrm: string;
  readonly postcode: string;
  readonly latitude?: number;
  readonly longitude?: number;
  readonly campaignId?: string | null;
  readonly partner?: JSON;
  readonly sku?: string;
  readonly autoAddToBasket: boolean;
  readonly activeExperiments?: string;
}

export type VrmLookupOnSubmitData = {
  vrm: string;
  postcode: string;
  latitude?: number;
  longitude?: number;
  activeTab?: string;
};
export function useVrmLookup({
  extraParams,
  formAction,
  pageType,
  sku,
  targetBlank,
  activeTab,
}: UseVrmLookupProps): any {
  const searchParams = useMemo(() => {
    if (isBrowser) {
      const parse = new URLSearchParams(window.location.search);
      const campaignIdFromUrl = parse.get('campaignId');
      const tier = parse.get('tier');
      return { campaignId: campaignIdFromUrl, tierKapten: tier };
    }
    return {};
  }, []);

  const {
    location: { pathname },
    locale,
    meta = {},
  } = useAppContext<
    AppContextGlobal & {
      readonly meta: AnyObject;
    }
  >();

  const segment = getSegmentFromLocale(locale);

  const onSubmit = useCallback(
    async ({ vrm, postcode, latitude, longitude }: VrmLookupOnSubmitData) => {
      storeStartSessionUrl();

      const skuFromPage = pageType?.find((item: PageTypeProps) => item.type === 'service')?.sku;
      const hasAutoAdd = pageType?.find((item: { readonly type: string }) => item.type === 'service');
      const { campaignId, tierKapten } = searchParams;

      const defaultParams = {
        vrm: trimSpacesFromString(vrm),
        postcode: trimSpacesFromString(postcode),
        latitude,
        longitude,
        campaignId: getCampaignId(pathname, locale, campaignId || meta?.campaignIdFromCode, tierKapten),
        partner: safeCheckPartnerNameFromCookie(),
        sku: sku || skuFromPage,
        autoAddToBasket: Boolean((hasAutoAdd && 'autoAddToBasket' in hasAutoAdd) || undefined),
        activeExperiments: getActiveExperiments(),
        activeTab,
      };
      let params: SessionDefaultParams = defaultParams;
      if (extraParams) {
        params = {
          ...defaultParams,
          ...extraParams,
        };
      }
      const query = qs.stringify(params);
      if (targetBlank) {
        window.open(`${formAction}?${query}`, '_blank');
      } else {
        window.location.href = `${formAction}?${query}`;
      }
    },
    [
      extraParams,
      formAction,
      pageType,
      sku,
      targetBlank,
      locale,
      meta?.campaignIdFromCode,
      searchParams,
      pathname,
      activeTab,
    ]
  );

  return { onSubmit, segment };
}
